import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, BoltIcon, LockClosedIcon } from '@heroicons/react/24/outline';

/**
 * Token Alert Banner Component
 * Displays alerts when user is approaching or has exceeded token quota
 */
export const TokenAlertBanner = ({ userId, autoHide = true }) => {
  const [alert, setAlert] = useState(null);
  const [isVisible, setIsVisible] = useState(true);

  const fetchQuotaStatus = useCallback(async () => {
    try {
      const response = await fetch('/api/sro/quota', {
        credentials: 'include'
      });

      if (!response.ok) return;

      const quota = await response.json();

      // Determine alert type
      let alertData = null;
      const percentageUsed = parseFloat(quota.percentageUsed || 0);

      if (!quota.featureEnabled) {
        alertData = {
          type: 'exceeded',
          icon: LockClosedIcon,
          title: 'SRO Chat Disabled',
          message: quota.disabledReason || 'Your token quota has been exceeded.',
          color: 'red',
          action: { label: 'Upgrade Plan', href: '/pricing' }
        };
      } else if (percentageUsed >= 100) {
        alertData = {
          type: 'exceeded',
          icon: ExclamationTriangleIcon,
          title: 'Quota Exceeded',
          message: 'You have used all your monthly tokens. Upgrade to continue.',
          color: 'red',
          action: { label: 'Upgrade', href: '/pricing' }
        };
      } else if (percentageUsed >= 90) {
        alertData = {
          type: 'critical',
          icon: BoltIcon,
          title: 'Critical - Almost Out',
          message: `You have only ${quota.tokensRemaining?.toLocaleString()} tokens remaining.`,
          color: 'orange',
          action: { label: 'View Plans', href: '/pricing' }
        };
      } else if (percentageUsed >= 80) {
        alertData = {
          type: 'warning',
          icon: BoltIcon,
          title: 'Quota Running Low',
          message: `You've used ${percentageUsed.toFixed(0)}% of your monthly quota.`,
          color: 'amber',
          action: { label: 'View Plans', href: '/pricing' }
        };
      }

      setAlert(alertData);

      // Auto-hide warning alerts after 10 seconds
      if (autoHide && alertData && (alertData.type === 'warning')) {
        const timeout = setTimeout(() => {
          setIsVisible(false);
        }, 10000);
        return () => clearTimeout(timeout);
      }
    } catch (err) {
      console.error('Error fetching quota:', err);
    }
  }, [autoHide]);

  useEffect(() => {
    fetchQuotaStatus();
    const interval = setInterval(fetchQuotaStatus, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchQuotaStatus]);

  if (!alert || !isVisible) return null;

  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-800',
      badge: 'bg-red-100 text-red-800',
      button: 'bg-red-600 hover:bg-red-700 text-white'
    },
    orange: {
      bg: 'bg-orange-50',
      border: 'border-orange-200',
      text: 'text-orange-800',
      badge: 'bg-orange-100 text-orange-800',
      button: 'bg-orange-600 hover:bg-orange-700 text-white'
    },
    amber: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      text: 'text-amber-800',
      badge: 'bg-amber-100 text-amber-800',
      button: 'bg-amber-600 hover:amber-700 text-white'
    }
  };

  const colors = colorClasses[alert.color] || colorClasses.amber;
  const Icon = alert.icon;

  return (
    <div className={`token-alert-banner ${colors.bg} border ${colors.border} rounded-lg p-4 mb-4 ${colors.text}`}>
      <div className="flex items-start gap-3">
        {/* Icon */}
        <Icon className="w-5 h-5 mt-0.5 flex-shrink-0" />

        {/* Content */}
        <div className="flex-1">
          <div className="font-semibold text-sm mb-1">{alert.title}</div>
          <div className="text-sm mb-3">{alert.message}</div>

          {/* Action Button */}
          {alert.action && (
            <a
              href={alert.action.href}
              className={`inline-block px-3 py-2 rounded text-sm font-medium ${colors.button}`}
            >
              {alert.action.label}
            </a>
          )}
        </div>

        {/* Close Button */}
        <button
          onClick={() => setIsVisible(false)}
          className={`flex-shrink-0 p-1 rounded hover:bg-black hover:bg-opacity-10`}
          title="Dismiss"
        >
          <XMarkIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default TokenAlertBanner;
